import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { SessionGuard, broadcastLogout, onBroadcastLogout } from '@/components/SessionGuard';
import { LocationGuard, AdminDeviceAlert, markLocationSent } from '@/components/LocationGuard';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

// Tokens now live only in HttpOnly cookies — wipe anything left from the old localStorage scheme.
['access_token', 'refresh_token'].forEach(k => localStorage.removeItem(k));

const SESSION_ENDED_EVENT = 'rtr:session-ended';
const isAuthCall = url => /\/auth\/(login|refresh|logout|me)/.test(url || '');
let authed = false;

api.interceptors.response.use(
  res => res,
  async error => {
    if (error.response?.status === 409 && error.response?.data?.code === 'duplicate_address') {
      window.dispatchEvent(new CustomEvent('rtr:duplicate-address', { detail: error.response.data }));
    }
    if (error.response?.status === 401 && authed && !error.config._retry && !isAuthCall(error.config.url)) {
      error.config._retry = true;
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        return api(error.config);
      } catch (e) {
        window.dispatchEvent(new CustomEvent(SESSION_ENDED_EVENT, { detail: e.response?.data?.detail }));
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

function formatError(detail) {
  if (detail == null) return "Connection error. Please check your internet and try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

// Hard navigation drops every page's in-memory data so nothing stays on screen after logout.
function blankToLogin(reason) {
  sessionStorage.clear();
  const q = reason ? `?reason=${encodeURIComponent(reason)}` : '';
  window.location.replace(`/login${q}`);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/auth/me').then(r => { authed = true; setUser(r.data); }).catch(() => setUser(false)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const ended = e => {
      if (!user) return;
      broadcastLogout('expired');
      blankToLogin(e.detail || 'expired');
    };
    window.addEventListener(SESSION_ENDED_EVENT, ended);
    const off = onBroadcastLogout(reason => { if (user) blankToLogin(reason); });
    return () => { window.removeEventListener(SESSION_ENDED_EVENT, ended); off(); };
  }, [user]);

  const completeLogin = useCallback(async () => {
    const me = await api.get('/auth/me');
    authed = true;
    markLocationSent();
    setUser(me.data);
  }, []);

  const login = useCallback(async (email, password, location) => {
    try {
      const { data } = await api.post('/auth/login', { email, password, location: location || undefined });
      if (data.device_pending) return { success: true, pending: data };
      const me = await api.get('/auth/me');
      authed = true;
      if (location) markLocationSent();
      setUser(me.data || data);
      return { success: true };
    } catch (e) {
      const msg = e.response?.data?.detail
        ? formatError(e.response.data.detail)
        : e.message === 'Network Error'
          ? 'Cannot reach the server. Please check your connection and try again.'
          : e.message || 'Connection error. Please try again.';
      return { success: false, error: msg };
    }
  }, []);

  const register = useCallback(async (email, password, name, role) => {
    try {
      const { data } = await api.post('/auth/register', { email, password, name, role });
      return { success: true, data };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async (reason) => {
    authed = false;
    await api.post('/auth/logout').catch(() => {});
    const r = typeof reason === 'string' ? reason : '';
    broadcastLogout(r);
    blankToLogin(r);
  }, []);

  const refreshUser = async () => {
    try {
      const r = await api.get('/auth/me');
      setUser(r.data);
      return r.data;
    } catch { return null; }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, completeLogin, register, logout, api, refreshUser }}>
      {children}
      {user && <SessionGuard api={api} session={user.session} onTimeout={logout} />}
      {user && <LocationGuard api={api} session={user.session} onTimeout={logout} />}
      {user?.role === 'admin' && <AdminDeviceAlert api={api} />}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
